import type { AiCostLimitLevel } from '@Domain/AiCostLimits/AiCostLimitLevel';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import LimitRow from '@UI/Pages/Administration/AiCostLimits/Sections/LevelLimits/LimitRow';
import type AiCostLimitUsersListView from '@UI/Store/AiCostLimits/Views/AiCostLimitUsersListView';
import resolveSubjectName from '@UI/Pages/Administration/Shared/Helpers/resolveSubjectName';
import { openAiCostLimitCreation } from '@UI/Pages/Administration/Shared/Signals/aiCostLimitSidebarSignal';
import { css } from '@emotion/react';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { useSelector } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const sectionStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
`;

const headerStyle = css`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 0.75rem;
`;

const titleStyle = css`
	${theme.typography.sectionTitle}
	margin: 0;
	color: ${theme.colors.ink};
`;

const listStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
	margin: 0;
	padding: 0;
	list-style: none;
`;

const emptyTextStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkSecondary};
`;

type Props = {
	readonly level: Exclude<AiCostLimitLevel, 'account'>;
	readonly title: string;
	readonly emptyText: string;
	readonly addLabel: string;
	readonly users: AiCostLimitUsersListView | null;
};

/** The limits of one level. `users` is only given for the user level, to resolve the subjects' names. */
const LevelLimits: React.FC<Props> = ({ level, title, emptyText, addLabel, users }) => {
	const limits = useSelector((state) => state.aiCostLimitsStore.limits);
	const subjects = useSelector((state) => state.aiCostLimitsStore.subjects);

	const handleAddClick = React.useCallback(() => {
		openAiCostLimitCreation(level);
	}, [level]);

	const levelLimits = React.useMemo(() => limits?.ofLevel(level) ?? [], [limits, level]);

	return (
		<section css={sectionStyle}>
			<div css={headerStyle}>
				<h2 css={titleStyle}>{title}</h2>
				<Button
					type="button"
					severity="secondary"
					label={addLabel}
					icon={<Plus size={16} />}
					onClick={handleAddClick}
				/>
			</div>
			<ConditionalLoader
				data={levelLimits.length > 0}
				fallback={<p css={emptyTextStyle}>{emptyText}</p>}
			>
				<ul css={listStyle}>
					{levelLimits.map((limit) => (
						<li key={limit.id}>
							<LimitRow
								limit={limit}
								subjectName={resolveSubjectName(level, limit.subjectId, subjects, users)}
							/>
						</li>
					))}
				</ul>
			</ConditionalLoader>
		</section>
	);
};

export default LevelLimits;
